import { padStart } from 'lodash';
import { Slot } from './slot';

export class RadixSlot extends Slot {
    private digits: number; // The number of characters for string representation (e.g., for padding)
    private max: number; // The maximum numerical value for this slot

    // The constructor now takes the raw definition values and calculates the correct properties internally.
    constructor(
        bitWidth: number, // The width of the value in bits.
        position: number, // The position from the definition (not used for max/digits calculation).
        radix: number, // The number base (e.g., 10 for decimal, 16 for hexadecimal).
    ) {
        super('', 0, radix);

        // The maximum numerical value is 2^width - 1.
        this.max = 2 ** bitWidth - 1;

        // The number of display digits is ceil(width / log2(radix)).
        // We ensure a minimum of 1 digit.
        this.digits = Math.ceil(bitWidth / Math.log2(radix)) || 1;
        const radixCharSets = {
            2: '[0-1]',
            8: '[0-7]',
            10: '[0-9]',
            16: '[0-9a-fA-F]',
            32: '[0-9a-vA-V]',
        };

        const radixCharSet = radixCharSets[this.base] || '.';
        const mask = new RegExp(`^${radixCharSet}{0,${this.digits}}$`);

        this.inputMask = {
            mask,
            postprocessors: [
                ({ value, selection }) => {
                    const rawValue = this.toRaw(value);
                    if (rawValue === undefined) {
                        return { value, selection }; // Let maskito handle invalid format
                    }
                    const floatValue = this.toEngrFloat(rawValue);
                    if (floatValue !== undefined && floatValue > this.engrMax) {
                        const clampedValue = this.toEngrString(this.toRaw(this.engrMax));
                        return {
                            value: clampedValue || '',
                            selection: [0, (clampedValue || '').length],
                        };
                    }
                    return { value, selection };
                },
            ],
        };
    }

    toEngrFloat = (value: number | undefined): number | undefined => {
        if (value === undefined || isNaN(value)) {
            return undefined;
        }
        return (value & this.max) >>> 0;
    };

    toEngrString = (value: number | undefined): string | undefined => {
        if (value === undefined || isNaN(value)) {
            return undefined;
        }
        const v = (value & this.max) >>> 0;
        return padStart(v.toString(this.base), this.digits, '0').toUpperCase();
    };

    toRawFloat = (value: number | string | undefined): number | undefined => {
        if (typeof value === 'undefined' || (typeof value === 'number' && isNaN(value))) {
            return undefined;
        }
        return this.engrNumOrStringToNum(value);
    };

    rawInRange = (value: number | undefined): boolean => typeof value === 'number' && value >= 0 && value <= this.max;

    engrInRange = (value: number | string | undefined): boolean => {
        const engrConv: number | undefined = this.engrNumOrStringToNum(value);
        return engrConv !== undefined && engrConv >= 0 && engrConv <= this.max;
    };

    engrNumOrStringToNum = (value: number | string | undefined): any => {
        let engrConv: number | undefined;
        if (typeof value === 'string') {
            engrConv = parseInt(value, this.base);
        } else if (typeof value === 'number') {
            engrConv = value;
        }
        return engrConv;
    };

    get rawMin() {
        return 0;
    }
    get rawMax() {
        return this.max;
    }
    get rawStep() {
        return 1;
    }
    get engrMin() {
        return 0;
    }
    get engrMax() {
        return this.max;
    }
    get engrStep() {
        return 1;
    }
    get rawIsInt() {
        return true;
    }
}
